import { defineStore } from 'pinia';
import { ref } from 'vue';

import useUserComposable from '@/composables/UserComposable';

import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';

const ASSESSMENT_URL = 'v2/template/assessment';
const ASSESSMENT_CATEGORY_URL = 'v2/template/assessment/categories';

export const useAssessmentStore = defineStore('assessment', () => {
    const { company_id } = useUserComposable();
    // states
    const loading = ref(false);
    const params = ref({
        categories: null,
        code: null,
        code_exact: null,
        default: true,
        company_id: null,
    });
    const editAssessmentId = ref(null);
    const assessment = ref({
        code: null,
        name: null,
        description: null,
        category_id: null,
        company_id: null,
        draft: false,
    });
    const stepBlocks = ref([]);
    const assessments = ref([]);
    const categories = ref([]);
    const assessmentStructure = ref({
        data: null,
        message: null,
    });

    // helper to reset the assessment value
    const initAssessment = async () => {
        editAssessmentId.value = null;
        assessment.value = {
            code: null,
            name: null,
            description: null,
            category_id: null,
            company_id: company_id.value,
            draft: false,
        };
        await fetchAllAssessments();
    };

    // this function lists out all the blocks for an assessment related through its steps
    // this is for temporary purpose of visualizing the blocks in AssessmentTable view details of assessment
    const getAssessmentBlocks = (currAssessment) => {
        // initialize the stepBlocks to null values
        stepBlocks.value = [];
        currAssessment?.steps?.forEach((step) => {
            // push each blocks from the steps into the array
            stepBlocks.value?.push(step.block);
        });
    };

    const fetchAllAssessments = async (userProfileFlag = false) => {
        try {
            loading.value = true;
            return await GlobalRequest.getRequest(ASSESSMENT_URL, {
                params: {
                    ...params.value,
                    company_id: company_id.value,
                },
            }).then((response) => {
                const filteredAssessments = userProfileFlag ? response.data : response.data?.filter((assessment) => assessment.code !== 'user_profile');
                assessments.value = typeof filteredAssessments === 'object' ? Object.values(filteredAssessments) : filteredAssessments;
                return assessments.value;
            });
        } catch (error) {
            showToast(error.response, 'Failed to fetch all assessments');
        } finally {
            loading.value = false;
        }
    };

    const fetchAssessmentCategories = async () => {
        try {
            await GlobalRequest.getRequest(`${ASSESSMENT_CATEGORY_URL}?company_id=${company_id.value}`).then((response) => {
                categories.value = response.data;
            });
        } catch (error) {
            showToast(error.response, 'Failed to fetch assessments categories');
        }
    };

    const fetchUserAssessment = async (params) => {
        try {
            // New version of user profile assessment is created everytime a user profile attribute is created
            // so, after listing all the user profile assessment, choose the latest and default one
            // there is only one default user_profile assessment at a time
            await GlobalRequest.getRequest(`${ASSESSMENT_URL}`, {
                params: {
                    company_id: company_id.value,
                    default: params.default,
                    categories: params.categories,
                    code: params.code,
                    code_exact: params.code_exact,
                },
            }).then((response) => {
                const userAssessment = response.data?.find((assessment) => assessment.default === 1);
                assessment.value = userAssessment;
                return userAssessment;
            });
        } catch (error) {
            showToast(error.response, 'Failed to fetch user assessment form');
        }
    };

    const addAssessment = async (body) => {
        try {
            return await GlobalRequest.postRequest(`${ASSESSMENT_URL}`, {
                ...body,
                company_id: company_id.value,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
            }).then((response) => {
                if (response.statusCode === 201) {
                    showToast(response, 'Assessment created successfully');
                    initAssessment();
                    return response.data;
                }
            });
        } catch (error) {
            showToast(error, error?.data?.message ?? 'Failed to add assessment');
        }
    };

    const editAssessment = async (assessmentId) => {
        const currAssessment = assessments.value.find((assessment) => assessment.id === assessmentId);

        // if there is assessment data then populate them into the assessment
        if (currAssessment) {
            editAssessmentId.value = currAssessment.id;
            assessment.value = {
                code: currAssessment.code,
                name: currAssessment.name,
                description: currAssessment.description,
                category_id: currAssessment.category_id,
                company_id: company_id.value,
                draft: currAssessment.draft,
            };
        }
    };

    const updateAssessment = async (id, body) => {
        try {
            return await GlobalRequest.patchRequest(`${ASSESSMENT_URL}/${id}`, {
                ...body,
                company_id: company_id.value,
                description: body.description?.length > 255 ? body.description?.substring(0, 255) : body.description,
            }).then((response) => {
                if (response.statusCode === 200) {
                    showToast(response, 'Assessment updated successfully');
                    assessments.value.push(response.data);
                    initAssessment();
                    return response.data;
                }
            });
        } catch (error) {
            showToast(error.response, error?.data?.message ?? 'Failed to update assessment');
        }
    };

    const fetchAssessmentById = async (id) => {
        try {
            // first check in the existing assessments array state
            const existingAssessment = assessments.value?.find((assessment) => assessment.id === id);
            if (existingAssessment) {
                // console.log(existingAssessment);
                return existingAssessment;
            } else {
                return await GlobalRequest.getRequest(`${ASSESSMENT_URL}/${id}`).then((response) => {
                    if (response.statusCode == 200) {
                        // console.log(response.data);
                        assessment.value = response.data;
                        return response.data;
                    }
                });
            }
        } catch (error) {
            showToast(error.response, 'Failed to fetch assessment by id');
        }
    };

    const deleteAssessment = async (id) => {
        try {
            await GlobalRequest.deleteRequest(`${ASSESSMENT_URL}/${id}`, {
                params: {
                    company_id: company_id.value,
                },
            }).then((response) => {
                if (response.statusCode === 200) {
                    showToast(response, 'Assessment deleted successfully');
                    assessments.value = assessments.value.filter((assessment) => assessment.id !== id);
                }
            });
        } catch (error) {
            showToast(error.response, error?.data?.message ?? 'Failed to delete assessment');
        }
    };

    const setAssessmentDefault = async (id) => {
        try {
            await GlobalRequest.patchRequest(`${ASSESSMENT_URL}/${id}/default`).then((response) => {
                if (response.statusCode === 200) {
                    showToast(response, 'Assessment set to default successfully');
                    fetchAllAssessments();
                }
            });
        } catch (error) {
            showToast(error.response, 'Failed to delete assessment');
        }
    };

    // this function checks for the validity of an assessment links structure
    // returns a structure if valid else returns error messages as needed
    const getAssessmentStructure = async (id) => {
        try {
            const response = await GlobalRequest.getRequest(`${ASSESSMENT_URL}/${id}/structure`, {
                params: {
                    company_id: company_id.value,
                    assessment_id: id,
                },
            });
            // CASE 200: Structure is valid and return a valid array of data structure
            if (response.statusCode === 200) {
                assessmentStructure.value = {
                    data: response?.data?.structure,
                    header: 'Structure Valid',
                    message: 'Assessment structure is valid',
                };
            } else {
                assessmentStructure.value = {
                    data: response?.data?.structure,
                    header: 'Structure Invalid',
                    message: response?.data?.errors[0]?.description ?? 'Assessment structure is invalid',
                };
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to get assessment structure');
        }
    };

    // helpers
    function initParams() {
        params.value = {
            categories: [],
            code: null,
            code_exact: null,
            default: null,
            company_id: company_id.value,
        };
        return params.value;
    }

    const getAttributesGroupByBlocks = async (assessment_code, taggedAttributeFlag = false) => {
        // first get the  assessment id by its assessment_code from the existing assessment from the store
        const assessment_id = assessments.value?.filter((assessment) => assessment.code === assessment_code)[0]?.id;
        // if id exists and is valid, extract assessment details by id
        if (assessment_id) {
            const assessmentDetails = await fetchAssessmentById(assessment_id);
            // console.log(assessmentDetails);
            // now using the blocks from the assessment details, group the attributes by blocks
            const filteredBlocks = assessmentDetails?.steps?.filter((obj) => obj.block !== null)?.map((obj) => obj.block);
            const attributesByBlock = groupAttributesByBlock(filteredBlocks, taggedAttributeFlag);
            return { assessment_code: assessment_code, attributes: attributesByBlock };
        }
        return {};
    };

    const groupAttributesByBlock = (data, taggedAttributeFlag) => {
        let resData = [];
        // filter out blocks with no attributes
        const filteredData = data?.filter((obj) => obj.attributes?.length > 0);

        for (let item of filteredData) {
            let nodeData = {
                label: item.name,
                code: item.code,
                items: item.attributes
                    ?.filter((obj) => ![1, 2, 3].includes(parseInt(obj.type))) //filtering out free text types and
                    ?.filter((attribute) => (taggedAttributeFlag ? attribute.tags.length > 0 : attribute.tags.length === 0)) // filter out tagged attributes
                    ?.map((obj) => {
                        return {
                            id: obj.id,
                            label: obj?.label,
                            code: obj?.code,
                            tag_id: obj?.tags[0]?.tag_id, // adding tag id just in case we might need tag_id if an attribute is connected to one
                        };
                    }),
            };
            // if a block consists of only free texts, the items will be empty array so avoid pushing it
            if (nodeData.items.length > 0) {
                resData.push(nodeData);
            }
        }
        return resData;
    };

    return {
        company_id,
        // states
        loading,
        params,
        editAssessmentId,
        assessment,
        stepBlocks, // list of each block extracted from each step related to an assessment
        assessments,
        assessmentStructure,
        // actions
        fetchAllAssessments,
        fetchAssessmentCategories,
        fetchUserAssessment,
        addAssessment,
        editAssessment,
        updateAssessment,
        fetchAssessmentById,
        deleteAssessment,
        setAssessmentDefault,
        getAssessmentStructure,
        // helper functions
        initParams,
        getAssessmentBlocks,
        getAttributesGroupByBlocks,
    };
});
